import CloseIcon from '@mui/icons-material/Close';
import {
  AlertColor,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { AlertDialog, DialogResult } from 'src/components/analytics';
import AttentionBox from 'src/components/analytics/attention-box';
import StyledTextareaAutosize from 'src/components/analytics/form-controls/text-field/styled-text-area-field';
import StyledTextField from 'src/components/analytics/form-controls/text-field/styled-text-field';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import useModuleMetadata from 'src/hooks/use-module-metadata.hook';
import MODULE_TO_PAGE_MAP from '../../../home/types/simulation-module-to-page-map';
import { formatNumber } from '../../../pricing-simulator/utils/number-utils';
import { SKUPopupTitle } from '../../enums/sku-popup-enum';
import {
  resetOverwriteInputFields,
  updateOverwriteInputFields
} from '../../store/innovation-slice';
import { createOverwriteSKU } from '../../store/innovation-slice-async-thunks';
import { getFormattedSkuObject } from '../../utils/utils';
import SnackbarComponent from '../snackbar/snackbar';
import './index.scss';

function StyledLabel({ label }: { label: string }) {
  return (
    <Typography
      component='label'
      fontSize={10}
      fontWeight={400}
      textAlign='left'
    >
      {label}
    </Typography>
  );
}

export default function CreateOverwriteSKU(props: {
  isOverwriteDialogOpen: boolean;
  closeOverwriteDialog;
}) {
  const appDispatch = useAppDispatch();
  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [isAttentionBoxOpen, setAttentionBoxOpen] = useState(false);
  const { moduleId, scenarioId, sourceId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.INNOVATION
  });

  // local state for snackbar open/close
  const [isSnackbarOpen, setSnackbar] = useState(false);
  const [snackbarContent, setSnackbarContent] = useState<{
    snackbarText: string;
    snackbarType: AlertColor;
  }>({
    snackbarText: '',
    snackbarType: 'success'
  });

  const alertMessageObj = {
    missingFieldsMessage: 'Please provide values for all required fields',
    negativeValuesMessage: 'Invalid input. Please enter a valid numeric value.',
    duplicateValueMessage:
      'Overwritten and Simulate Units cannot be same, please enter different value.'
  };

  // local state for alert error messge
  const [alertMessage, setAlertMessage] = useState(
    alertMessageObj.missingFieldsMessage
  );

  // local state for units_mo
  const [moValues, setMoValues] = useState({
    innovation_sales_units_mo: 0,
    innovation_nsv_mo: 0,
    innovation_mac_mo: 0,
    innovation_mac_perc_mo: 0
  });
  const [moValuesBase, setMoValuesBase] = useState({
    innovation_sales_units_mo: 0,
    innovation_nsv_mo: 0,
    innovation_mac_mo: 0,
    innovation_mac_perc_mo: 0
  });

  // local state for maintaining is editing
  const [isEditing, setIsEditing] = useState(false);

  // handler for displaying comma seperated value
  const showFormattedValue = (value) => {
    // Format the value with commas
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // handler for input change on focus
  const handleFocus = () => {
    //allow editing
    setIsEditing(true);
  };

  // handler for input change on blur
  const handleBlur = () => {
    // stop editing
    setIsEditing(false);
  };

  const {
    fetchingMetaData,
    fetchingOverwriteData,
    moduleValue,
    createCurrentOverwrittenObj,
    appliedFilters,
    prevSkuName,
    skuOverwriteFields
  } = useAppSelector((state) => state.innovation);

  const moduleInnovationFormFields = skuOverwriteFields;

  // text fields
  const textFieldItems = moduleInnovationFormFields.fields.filter((ele) =>
    ['sku', 'innovation_justification'].includes(ele.name)
  );

  // sim result radio
  const simResultRadio = moduleInnovationFormFields.fields.filter(
    (ele) => ele.section === 'sim_result' && ele.control_type === 'RADIO'
  );

  // sim result items other than radio
  const simResultItems = moduleInnovationFormFields.fields
    .filter(
      (ele) =>
        ele.section === 'sim_result' &&
        ele.control_type !== 'RADIO' &&
        ele.name !== 'sku'
    )
    .sort((a, b) => (a.placement_order > b.placement_order ? 1 : -1));

  // manual overwrite radio
  const manualOverwriteRadio = moduleInnovationFormFields.fields.filter(
    (ele) => ele.section === 'manual_overwrite' && ele.control_type === 'RADIO'
  );

  // manual overwrite items other than radio
  const manualOverwriteItems = moduleInnovationFormFields.fields
    .filter(
      (ele) =>
        ele.section === 'manual_overwrite' &&
        ele.control_type !== 'RADIO' &&
        ele.name !== 'innovation_justification'
    )
    .sort((a, b) => (a.placement_order > b.placement_order ? 1 : -1));

  const [radioValue, setRadioValue] = useState('');
  const [currentSection, setCurrentSection] = useState('');
  const radioValueToSectionMapping = {
    simResultRadio: 'sim_result',
    ManualOverwriteRadio: 'manual_overwrite'
  };

  /**
   * handler for formatting number as per the number format
   */
  const formatValues = (field) => {
    if (field.number_format !== '') {
      return formatNumber(
        field.number_format,
        Number(createCurrentOverwrittenObj[field.name]),
        field.prefix_label,
        field.suffix_label
      );
    }

    return Math.floor(createCurrentOverwrittenObj[field.name]);
  };

  // on load setting the radio button as per the innovation_is_mo_applied flag
  useEffect(() => {
    if (skuOverwriteFields.fields.length > 1) {
      const isImoApplied = skuOverwriteFields.fields.find(
        (ele) => ele.name === 'innovation_is_mo_applied'
      ).values[0];
      // getting the radio value as per the innovation_is_mo_applied
      const currRadioValue =
        isImoApplied === 0 ? 'simResultRadio' : 'ManualOverwriteRadio';
      // setting the radio value
      setRadioValue(currRadioValue);
      // as per the radio value setting the current section i.e sim_result or manual_overwrite
      setCurrentSection(radioValueToSectionMapping[currRadioValue]);

      // setting the local state for mo values
      const moKeys = skuOverwriteFields.fields
        .filter(
          (e) =>
            e.section === 'manual_overwrite' &&
            (e.control_type === 'NUMBER' || e.control_type === 'PERCENTAGE')
        )
        .map((ele) => ele.name);

      // formatting the MO values as per the number format and storing it in local state
      const updatedMOValues = moKeys.reduce(
        (acc, key) => {
          acc[key] = formatValues(
            skuOverwriteFields.fields.filter((ele) => ele.name === key)[0]
          );
          return acc;
        },
        { ...moValues }
      );
      // setting the default values of manual overwrite on load of IMO popup
      setMoValues(updatedMOValues);
      setMoValuesBase(updatedMOValues);
    }
  }, [skuOverwriteFields]);

  // updating the current section on change of radio button
  useEffect(() => {
    setCurrentSection(radioValueToSectionMapping[radioValue]);
  }, [radioValue]);

  // handler for radio change
  function handleRadioChange(e) {
    const newValue = e.target.value;
    setRadioValue((prevValue) => newValue);
  }

  /**
   * handler for on change of input values
   */
  const handleOnChange = (name, value, controlType = '') => {
    // setting the local state of MO values
    if (controlType.toLowerCase() === 'number') {
      setMoValues({ ...moValues, [name]: value });
    }
    // calling dispatcher for setting input values as per name & value
    appDispatch(updateOverwriteInputFields({ name, value }));
  };

  const handleSubmit = () => {
    // values are filtered based on current section/radio selected,
    // so these fileds should not be empty while submitting
    const filteredValue = moduleInnovationFormFields.fields
      .filter(
        (val) =>
          val.control_type !== 'RADIO' &&
          (val.section === currentSection || val.section === '') &&
          val.is_active == true
      )
      .map((item) => item.name);

    const newEditedFields = getFormattedSkuObject(
      createCurrentOverwrittenObj,
      moduleInnovationFormFields.fields,
      moduleValue
    );
    // updating innovation_is_mo_applied flag
    newEditedFields.innovation_is_mo_applied =
      radioValue === 'ManualOverwriteRadio' ? 1 : 0;

    // checking if all the fields are filled
    if (filteredValue.filter((v) => newEditedFields[v] === '').length === 0) {
      // checking if all the values are positive
      if (filteredValue.filter((ele) => newEditedFields[ele] <= 0).length > 0) {
        setAlertMessage(alertMessageObj.negativeValuesMessage);
        setAttentionBoxOpen(true);
        return null;
      }
      // checking if manual overwritten and simulation results values are same
      const salesUnitsKeys = Object.keys(newEditedFields).filter((ele) =>
        ele.includes('sales_units')
      );

      if (
        Math.floor(createCurrentOverwrittenObj[salesUnitsKeys[0]]) ===
        Math.floor(createCurrentOverwrittenObj[salesUnitsKeys[1]])
      ) {
        setAlertMessage(alertMessageObj.duplicateValueMessage);
        setAttentionBoxOpen(true);
        return null;
      }
      return appDispatch(
        createOverwriteSKU({
          sourceID: sourceId,
          moduleID: moduleId,
          scenarioID: Number(scenarioId),
          skuName: prevSkuName,
          editedSkuObj: { ...createCurrentOverwrittenObj, ...newEditedFields },
          filters: appliedFilters
        })
      ).then((res: any) => {
        if (res.meta.requestStatus === 'rejected') {
          setSnackbarContent({
            // eslint-disable-next-line dot-notation
            snackbarText: res['error'].message,
            snackbarType: 'error'
          });
          setSnackbar(true);
          props.closeOverwriteDialog();
        }
        props.closeOverwriteDialog();
      });
    }
    setAlertMessage(alertMessageObj.missingFieldsMessage);
    setAttentionBoxOpen(true);
  };

  const handleCloseOnIconClick = () => {
    setConfirmationDialogOpen(true);
  };
  // function to handle confirmation dialogue close
  const handleConfirmationDialogClose = (result: DialogResult) => {
    if (result === DialogResult.YES) {
      handleClose(event, 'reason', false);
    }
    setConfirmationDialogOpen(false);
  };

  const handleAttentionBoxClose = () => {
    setAttentionBoxOpen(false);
  };

  const handleClose = (
    event,
    reason,
    isSnackbarToOpen,
    snackbarText = '',
    snackbarType = ''
  ) => {
    if (reason && reason === 'backdropClick') return;
    // setCreateDialog(isSnackbarToOpen, snackbarText, snackbarType);
    // appDispatch(closeSkuPop(false));
    props.closeOverwriteDialog();
  };

  /**
   * handler for resetting the values
   */
  const handleResetValues = () => {
    //  resestting mo values using base state
    setMoValues({ ...moValues, ...moValuesBase });
    // dispatching action for resetting mo values in store
    appDispatch(resetOverwriteInputFields());
  };

  return (
    <>
      <Dialog
        fullWidth
        maxWidth='md'
        open={props.isOverwriteDialogOpen}
        onClose={props.closeOverwriteDialog}
        disableEscapeKeyDown
      >
        <DialogTitle id='alert-dialog-title' px={2}>
          <p>{SKUPopupTitle.overwrite}</p>
          <IconButton
            aria-label='close'
            onClick={handleCloseOnIconClick}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '20px 28px' }}>
          <>
            {fetchingOverwriteData ? (
              <Grid
                container
                spacing={1}
                alignItems='center'
                justifyContent='center'
                mt={1}
              >
                <CircularProgress />
              </Grid>
            ) : (
              <>
                {textFieldItems
                  .filter((field) => field.name === 'sku')
                  .map((field) => (
                    <Grid
                      item
                      xs={12}
                      sx={{ position: 'relative', top: '7px' }}
                    >
                      <StyledLabel label={field.alias} />
                      <StyledTextField
                        type='text'
                        disabled
                        size='small'
                        variant='outlined'
                        key={field.name}
                        value={createCurrentOverwrittenObj[field.name]}
                        onChange={(e: any) =>
                          handleOnChange(field.name, e.target.value)
                        }
                        fullWidth
                      />
                    </Grid>
                  ))}
                <Grid item xs={12} sx={{ position: 'relative' }}>
                  <FormControl component='fieldset'>
                    <RadioGroup
                      aria-label='radiobtn'
                      name='radiobtn'
                      value={radioValue}
                      onChange={(e: any) => handleRadioChange(e)}
                    >
                      {simResultRadio[0].control_type == 'RADIO' ? (
                        <FormControlLabel
                          value={simResultRadio[0].name}
                          control={<Radio />}
                          label={simResultRadio[0].alias}
                          disabled={!simResultRadio[0].is_active}
                          style={{ paddingTop: '10px' }}
                        />
                      ) : null}
                      <Grid container spacing={1} alignItems='center'>
                        {simResultItems
                          .filter((field) => field.is_visible)
                          .map((field) => (
                            <>
                              {field.control_type !== 'RADIO' ? (
                                <Grid
                                  item
                                  xs={12}
                                  sm={3}
                                  style={{ paddingTop: '0 !important' }}
                                >
                                  <StyledLabel label={field.alias} />
                                  <div>
                                    <StyledTextField
                                      type='text'
                                      disabled={!field.is_active}
                                      size='small'
                                      variant='outlined'
                                      key={field.name}
                                      value={showFormattedValue(
                                        formatValues(field)
                                      )}
                                      onChange={(e) =>
                                        handleOnChange(
                                          field.name,
                                          Number(e.target.value)
                                        )
                                      }
                                    />
                                  </div>
                                </Grid>
                              ) : null}
                            </>
                          ))}
                      </Grid>

                      {manualOverwriteRadio[0].control_type == 'RADIO' ? (
                        <div className='flex-div'>
                          <FormControlLabel
                            value={manualOverwriteRadio[0].name}
                            control={<Radio />}
                            label={manualOverwriteRadio[0].alias}
                            disabled={!manualOverwriteRadio[0].is_active}
                            style={{ paddingTop: '10px' }}
                          />
                          <div className='mandatory-fields-text'>
                            * Mandatory Fields
                          </div>
                        </div>
                      ) : null}

                      <Grid container spacing={1} alignItems='center'>
                        {manualOverwriteItems
                          .filter((field) => field.is_visible)
                          .map((field) => (
                            <>
                              {field.control_type !== 'RADIO' ? (
                                <Grid
                                  item
                                  xs={12}
                                  sm={3}
                                  style={{ paddingTop: '0 !important' }}
                                >
                                  <StyledLabel
                                    label={`${field.alias} ${
                                      field.is_active ? ' *' : ''
                                    }`}
                                  />
                                  {isEditing && field.is_active ? (
                                    <div>
                                      <StyledTextField
                                        type='number'
                                        disabled={!field.is_active}
                                        size='small'
                                        variant='outlined'
                                        key={field.name}
                                        value={moValues[field.name]}
                                        onChange={(e) =>
                                          handleOnChange(
                                            field.name,
                                            Number(e.target.value),
                                            field.control_type
                                          )
                                        }
                                        onBlur={handleBlur}
                                      />
                                    </div>
                                  ) : (
                                    <div>
                                      <StyledTextField
                                        type='text'
                                        disabled={!field.is_active}
                                        size='small'
                                        variant='outlined'
                                        key={field.name}
                                        value={showFormattedValue(
                                          moValues[field.name]
                                        )}
                                        onFocus={handleFocus}
                                      />
                                    </div>
                                  )}
                                </Grid>
                              ) : null}
                            </>
                          ))}
                      </Grid>
                    </RadioGroup>
                  </FormControl>
                </Grid>
                {textFieldItems
                  // filter based on field.name contains justification string
                  .filter((field) => field.name.includes('justification'))

                  .map((field) => (
                    <Grid
                      item
                      xs={12}
                      sx={{ position: 'relative', paddingTop: '10px' }}
                    >
                      <StyledLabel
                        label={`${field.alias} ${field.is_active ? ' *' : ''}`}
                      />
                      <StyledTextareaAutosize
                        sx={{
                          width: '100%',
                          resize: 'vertical',
                          fontSize: '10px',
                          maxHeight: '150px',
                          padding: '10px',
                          minHeight: '30px',
                          fontFamily:
                            'MarsCentra,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol",system-ui'
                        }}
                        disabled={!field.is_active}
                        key={field.name}
                        value={createCurrentOverwrittenObj[field.name]}
                        onChange={(e) =>
                          handleOnChange(field.name, e.target.value)
                        }
                        placeholder='Please provide rational for manual overwrite'
                      />
                    </Grid>
                  ))}
              </>
            )}
            <AlertDialog
              closeHandler={handleConfirmationDialogClose}
              isDialogOpen={isConfirmationDialogOpen}
              message='Inputs will be reset and all changes to will be lost. Continue?'
              title='Cancel'
            />
            <AttentionBox
              closeHandler={handleAttentionBoxClose}
              isBoxOpen={isAttentionBoxOpen}
              message={alertMessage}
              title='Alert!'
            />
          </>
        </DialogContent>
        <DialogActions
          sx={{ boxShadow: '0px -2.84583px 2.84583px rgba(0, 0, 0, 0.04)' }}
        >
          <Button variant='outlined' onClick={handleResetValues}>
            Reset
          </Button>
          <Button variant='contained' onClick={handleCloseOnIconClick}>
            Close
          </Button>
          <Button variant='contained' onClick={handleSubmit}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <SnackbarComponent
        isSnackbarOpen={isSnackbarOpen}
        setSnackbar={setSnackbar}
        snackbarText={snackbarContent.snackbarText}
        snackbarType={snackbarContent.snackbarType}
      />
    </>
  );
}
